import { Dialog as HDialog } from "@headlessui/react"
import { PropsWithChildren, useMemo, useRef } from "react"
import { useClickOutside } from "~/ui-rtk/shared/hooks"

type TDialogClasses = {
  container: string
}

export type TDialogProps = {
  isOpen: boolean
  handleClose: () => void
  width?: number | string
  height?: number | string
  classes?: Partial<TDialogClasses>
  closeOnClickOutside?: boolean
} & React.HTMLProps<HTMLDivElement>

export default function Dialog({
  isOpen,
  handleClose,
  children,
  width = 300,
  height = "auto",
  classes,
  closeOnClickOutside = false,
  ...divProps
}: PropsWithChildren<TDialogProps>) {
  const dialogRef = useRef<HTMLDivElement>(null)

  useClickOutside(dialogRef, event => {
    // Nested Headless UI portals (e.g. a MultiSelect panel anchored from
    // inside this dialog) live outside dialogRef in the DOM but are logically
    // inside the dialog — clicking them must not dismiss it. The dialog's OWN
    // portal wrapper also matches the selector, so only skip when the clicked
    // portal is a different one (it doesn't contain this dialog).
    const target = event.target as Element | null
    const portal = target?.closest?.("[data-headlessui-portal]")
    if (portal && dialogRef.current && !portal.contains(dialogRef.current)) {
      return
    }
    if (closeOnClickOutside) {
      handleClose()
    }
  })

  const divStyle = useMemo(
    () => ({
      width,
      ...(height !== "auto" ? { height } : {}),
    }),
    [width, height],
  )

  // Headless UI fires onClose on backdrop click and Escape; the backdrop div fires
  // onClick. Both need to be gated by `closeOnClickOutside` so the prop actually means
  // "the only way to close this is by an explicit button".
  const noop = () => undefined
  const handleOutside = closeOnClickOutside ? handleClose : noop

  return (
    <HDialog
      className="relative z-[999999999]"
      open={isOpen}
      onClose={handleOutside}
    >
      <div
        onClick={handleOutside}
        className="fixed inset-0 bg-black/50"
        aria-hidden="true"
      />
      <div className="fixed inset-0 overflow-y-auto">
        <HDialog.Panel className="flex items-center justify-center h-full min-h-full p-6 text-center">
          <div
            ref={dialogRef}
            style={divStyle}
            className={classes?.container}
            {...divProps}
          >
            {children}
          </div>
        </HDialog.Panel>
      </div>
    </HDialog>
  )
}
